
'use client';

import { useState, useEffect } from 'react';
import { GradientText } from '@/components/ui/gradient-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Image from 'next/image';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';

interface Partner {
  id: number;
  name: string;
  logo_url: string;
  website_url: string;
  description: string;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL;

async function fetchPartners(): Promise<{
  success: boolean;
  data?: Partner[];
  message?: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/partners`);
     if (!response.ok) {
        throw new Error('Failed to fetch partners');
    }
    const result = await response.json();
    return { success: result.success, data: result.data, message: result.message };
  } catch (error) {
    console.error("Error fetching partners:", error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
    return { success: false, message: errorMessage };
  }
}

export default function PartnersPage() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadPartners() {
      setIsLoading(true);
      const { success, data, message } = await fetchPartners();
      if (success && data) {
        setPartners(data);
      } else {
        setError(message || 'Could not load partners.');
      }
      setIsLoading(false);
    }
    loadPartners();
  }, []);


  return (
    <div className="container mx-auto max-w-5xl py-12 px-4">
      <div className="text-center mb-12">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Our <GradientText>Partners</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          We collaborate with industry leaders to deliver the best solutions.
        </p>
      </div>

       {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : error ? (
         <div className="text-center text-destructive-foreground bg-destructive/80 p-4 rounded-md">
            <p className="font-bold">Failed to load partners.</p>
            <p className="text-sm">{error}</p>
        </div>
      ) : partners.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {partners.map((partner) => (
            <Link key={partner.id} href={partner.website_url} target="_blank" rel="noopener noreferrer" className="block h-full">
              <Card className="flex h-full flex-col items-center text-center transition-all hover:shadow-primary/20 hover:shadow-lg hover:-translate-y-1">
                <CardHeader>
                  <Image
                    src={partner.logo_url}
                    alt={`${partner.name} Logo`}
                    width={150}
                    height={80}
                    className="object-contain"
                    data-ai-hint="company logo"
                  />
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardTitle className="mb-2">{partner.name}</CardTitle>
                  <p className="text-muted-foreground">{partner.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center text-muted-foreground py-16">
          <p className="text-lg">Our partner network is growing. Check back soon!</p>
        </div>
      )}
    </div>
  );
}
